import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ColumnSettings, ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { TableColumnListValue } from '@makyoapp/makyo-common'

import { IDBTable } from '@/core/db'
import { LookupCell } from '@/core/models/columns/lookup'
import cookies from '@/lib/cookies'
import { queryClient, queryKeys } from '@/lib/react-query'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

interface DataList {
  label: string
  value: any
  isIcon: boolean
  isImage: boolean
  isImageArray: boolean
  isUrl: boolean
  isRating: boolean
  isQrCode: boolean
  isBarcode: boolean
  ratingSettings?: ColumnSettings
}

function loadSingleRowData(
  table: IDBTable,
  dataSource: TableColumnListValue,
  language: string,
  userSpecific: string,
  currentUserId: string | undefined,
  rowId: string | undefined
): DataList[] {
  const { columns, filterBySession, isDetailPageTable } = dataSource

  const isUserTable = table.id === window.appUserTableID

  // Add early return if no columns
  if (!columns?.length) {
    return []
  }

  // initialize data list
  let dataList: DataList[] = []

  // handle user table
  if (isUserTable && currentUserId && (!rowId || rowId === currentUserId)) {
    // find currentUserData
    const userData = table.GetRowDataByRowId(currentUserId, language, userSpecific)
    if (!userData) return []

    // assign current user to dataList
    dataList = (columns || [])
      .map((col) => {
        if (!col.col) return
        let tableColumn = table.columnsMap.get(col.col)
        let columnType = tableColumn?._Type
        if (!tableColumn) return

        return {
          label: col.label,
          value: userData[tableColumn._id].toString(),
          isIcon: columnType === ColumnTypes.Icon,
          isImage: columnType === ColumnTypes.Image,
          isImageArray: columnType === ColumnTypes.ImageArray,
          isUrl: columnType === ColumnTypes.URL,
          isRating: columnType === ColumnTypes.Rating,
          isQrCode: columnType === ColumnTypes.QRCode,
          isBarcode: columnType === ColumnTypes.Barcode,
          ratingSettings: tableColumn._Settings,
        }
      })
      .filter((d) => !!d && !!d?.value) as DataList[]
  }

  // handle detail page table
  else {
    const shouldFilterBySession = filterBySession && table.isSessionTable

    const rowIdx = isDetailPageTable && rowId ? table.GetRowIndexByRowId(rowId, language, userSpecific) : 0

    const rowData = shouldFilterBySession
      ? table.GetRowDataBySessionId(cookies.getAppSessionId(window.appID), language, userSpecific)
      : table.GetRowData(rowIdx, language, userSpecific)

    if (!rowData) return []

    dataList = (columns || [])
      .map((dataColumn) => {
        // Handle custom column
        if (dataColumn.col === '#custom#') {
          return {
            label: dataColumn.label,
            value: dataColumn.customValue || '',
            isIcon: false,
            isImage: false,
            isImageArray: false,
            isUrl: false,
            isRating: false,
            isQrCode: false,
            isBarcode: false,
          }
        }

        if (!dataColumn.col) return null
        let tableColumn = table.columnsMap.get(dataColumn.col)
        let columnType = tableColumn?._Type
        if (!tableColumn) return null

        if (!rowData) return null
        let cellValue = rowData[tableColumn._id]
        if (!cellValue) return null

        let value = cellValue.toString()

        if (cellValue && columnType === ColumnTypes.Lookup && cellValue instanceof LookupCell) {
          columnType = cellValue.resolvedType
          value = cellValue?.toDisplayString() || ''
        }
        if (tableColumn._Type === ColumnTypes.SingleCell) {
          columnType = tableColumn._ResolvedType
        }

        return {
          label: dataColumn.label,
          value: columnType === ColumnTypes.Rating ? cellValue.value : value,
          isIcon: columnType === ColumnTypes.Icon,
          isImage: columnType === ColumnTypes.Image,
          isImageArray: columnType === ColumnTypes.ImageArray,
          isUrl: columnType === ColumnTypes.URL,
          isRating: columnType === ColumnTypes.Rating,
          isQrCode: columnType === ColumnTypes.QRCode,
          isBarcode: columnType === ColumnTypes.Barcode,
          ratingSettings: tableColumn._Settings,
        }
      })
      .filter((d) => !!d && !!d.value) as DataList[]
  }

  // set parsed data list
  return dataList
}

interface CompSingleRowDataParams {
  table: IDBTable | undefined
  componentId?: string
  dataSource: TableColumnListValue
  rowId: string | undefined
}

/**
 * Main hook to access data for single row component.
 */
export const useCompSingleRowData = ({ table, componentId, dataSource, rowId }: CompSingleRowDataParams) => {
  // get table id
  const tableId = table?.id

  const { dbWorker, language, userSpecific, loadedTables } = useDbClientContext()
  const { userId } = useAppAuthContext()

  // this act as both loading state and query dependency
  const tableStatus = !!tableId && loadedTables.has(tableId) ? 'READY' : 'PENDING'

  const memoizedParams = useMemo(() => {
    return [dataSource, language, userSpecific, userId, rowId] as const
  }, [dataSource, language, userSpecific, userId, rowId])

  // create a stable query key based on the current params
  const stableQueryKey = useMemo(() => {
    const [dataSource, language, userSpecific, userId, rowId] = memoizedParams
    return [
      queryKeys.compSingleRowData,
      `t:${tableId}`,
      `c:${componentId}`,
      `ds:${JSON.stringify(dataSource)}`,
      `ln:${language}`,
      `us:${userSpecific}`,
      `r:${rowId}`,
      `u:${userId}`,
      `ts:${tableStatus}`,
    ]
  }, [memoizedParams, tableId, componentId, tableStatus])

  /**
   * initialize query
   */
  const singleRowQuery = useQuery({
    queryKey: stableQueryKey,
    queryFn: () => {
      if (!table) return []
      return loadSingleRowData(table, ...memoizedParams)
    },
    enabled: tableStatus === 'READY',
    staleTime: Infinity, // keep data fresh to prevent additional fetch
  })

  // derived loading states
  const isLoadingData = singleRowQuery.isLoading
  const isLoading = tableStatus === 'PENDING' || isLoadingData

  /**
   * request table data from worker
   * @dbWorker
   */
  useEffect(() => {
    if (!dbWorker || !tableId) return

    const fetchData = () => {
      dbWorker.sendAction({
        type: 'get_table_data',
        payload: {
          table_id: tableId,
          language,
        },
        meta: { from: 'useCompSingleRowData' },
      })
    }

    fetchData()
    //
  }, [dbWorker, language, tableId])

  /**
   * subscribe to table data changes from db-worker
   */
  useEffect(() => {
    if (!table) return

    const unsubscribe = table.subscribe(() => {
      queryClient.invalidateQueries({ queryKey: stableQueryKey })
    })

    return () => {
      unsubscribe()
    }
  }, [stableQueryKey, table])

  return [singleRowQuery.data || [], isLoading] as const
}
